import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2, X, Clock, Check } from 'lucide-react';
import * as api from '../lib/api';
import { formatPrice, formatEventDate, formatEventTime } from '../lib/eventsFormat';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ event, onSuccess, busyRef }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [accepted, setAccepted] = useState(false);
  const [noPhotos, setNoPhotos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const amount = event.your_price_cents;
  const free = amount === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!accepted) { setError('Please confirm you are 18 or older and agree to the code of conduct.'); return; }
    if (!free && (!stripe || !elements)) return;
    setLoading(true);
    busyRef.current = true;
    setError('');
    try {
      let paymentMethodId;
      if (!free) {
        const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card', card: elements.getElement(CardElement),
        });
        if (pmError) { setError(pmError.message); return; }
        paymentMethodId = paymentMethod.id;
      }
      const res = await api.registerForEvent(event.id, { accepted_conduct: true, payment_method_id: paymentMethodId, photo_opt_out: noPhotos });
      const { status, requires_action: requiresAction, client_secret: clientSecret, payment_intent_id: intentId } = res.data;
      if (status === 'confirmed') { onSuccess(); return; }
      if (requiresAction && clientSecret) {
        const { error: confirmError } = await stripe.confirmCardPayment(clientSecret);
        if (confirmError) { setError(confirmError.message); return; }
        const fin = await api.finalizeEventRegistration(event.id, intentId);
        if (fin.data?.status === 'confirmed') { onSuccess(); return; }
        setError(fin.data?.status === 'refunded'
          ? 'Sorry, the event filled up while you were paying. You have been refunded in full.'
          : 'We could not confirm your payment. Please try again.');
        return;
      }
      setError('We could not complete your payment. Please try again.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
      busyRef.current = false;
    }
  };

  const discount = event.price_cents - amount;
  return (
    <form onSubmit={handleSubmit} noValidate data-testid="event-checkout-form">
      <div className="mt-4 text-[15px]">
        <div className="flex justify-between py-2.5 border-b border-dashed border-teal/30"><span>Ticket</span><span>{formatPrice(event.price_cents)}</span></div>
        {discount > 0 && (
          <div className="flex justify-between py-2.5 border-b border-dashed border-teal/30">
            <span>Sirati+ member discount ({event.premium_discount_pct}%)</span>
            <span className="font-bold text-teal-dark dark:text-teal-light">&minus;${(discount / 100).toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between pt-3.5 font-heading text-xl font-semibold"><span>Total</span><span data-testid="checkout-total">{free ? 'Free' : `$${(amount / 100).toFixed(2)}`}</span></div>
      </div>

      {!free && (
        <div className="mt-4">
          <label className="text-sm font-bold text-text-main mb-1.5 block" id="event-card-label">Card details</label>
          <div className="p-4 bg-white rounded-arch border border-teal/20" aria-labelledby="event-card-label">
            <CardElement options={{ style: { base: { fontSize: '16px', color: '#1a1a2e', '::placeholder': { color: '#999' } }, invalid: { color: '#ef4444' } } }} />
          </div>
        </div>
      )}

      <div className="mt-4 p-4 rounded-arch bg-lavender/15 text-[13px] leading-relaxed text-text-main">
        <strong>Cancellation:</strong> full refund up to {event.refund_window_hours} hours before the event.
        After that, tickets are not refundable. If we cancel the event, you are refunded in full.
      </div>

      <label htmlFor="event-conduct" className="flex gap-3 items-start mt-4 text-[13.5px] leading-relaxed text-text-main cursor-pointer">
        <input
          id="event-conduct" type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
          className="w-5 h-5 mt-0.5 shrink-0 accent-[#4AADA8]" data-testid="event-conduct-checkbox"
        />
        <span>
          I am 18 or older and agree to the Sirati community code of conduct and the{' '}
          <a href="/terms#events" target="_blank" rel="noopener noreferrer" className="text-primary underline">event terms</a>.
        </span>
      </label>

      <label htmlFor="event-no-photos" className="flex gap-3 items-start mt-3 text-[13.5px] leading-relaxed text-text-main cursor-pointer">
        <input
          id="event-no-photos" type="checkbox" checked={noPhotos} onChange={(e) => setNoPhotos(e.target.checked)}
          className="w-5 h-5 mt-0.5 shrink-0 accent-[#4AADA8]" data-testid="event-no-photos-checkbox"
        />
        <span>Please do not include me in event photos or video. <span className="text-text-muted">(Optional. You can also tell an organizer at check-in.)</span></span>
      </label>

      {error && <p className="text-sm text-red-500 mt-3" role="alert" data-testid="checkout-error">{error}</p>}

      <button
        type="submit"
        disabled={loading || (!free && !stripe)}
        className="w-full min-h-[48px] mt-5 py-3 bg-primary text-white rounded-full text-base font-bold btn-hover flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="event-pay-btn"
      >
        {loading
          ? <><Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Processing...</>
          : free ? 'Reserve my seat' : `Pay $${(amount / 100).toFixed(2)} and reserve my seat`}
      </button>
      <p className="text-xs text-text-muted text-center mt-3 flex items-center justify-center gap-1.5">
        <Clock className="w-3.5 h-3.5" aria-hidden="true" /> Your seat is held for 10 minutes while you pay.
      </p>
    </form>
  );
};

const SuccessView = ({ event, onDone }) => {
  const paid = event.your_price_cents > 0;
  return (
    <div className="text-center py-4" role="status" data-testid="event-success">
      <div className="rounded-arch-lg bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 px-5 py-7">
        <span className="mx-auto w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center">
          <Check className="w-7 h-7" strokeWidth={3} aria-hidden="true" />
        </span>
        <h2 id="event-checkout-title" tabIndex={-1} className="font-heading text-2xl font-semibold text-green-800 dark:text-green-200 mt-4 outline-none">
          {paid ? 'Payment received' : "You're registered"}
        </h2>
        <p className="text-green-800 dark:text-green-200 mt-1 font-medium">You&apos;re registered for {event.title}.</p>
        <p className="text-sm text-green-700 dark:text-green-300 mt-3">
          {paid ? 'Check your email for your receipt.' : 'The address is now on the event page.'}
        </p>
      </div>
      <button
        onClick={onDone}
        className="w-full min-h-[48px] mt-5 py-3 bg-primary text-white rounded-full text-base font-bold btn-hover"
        data-testid="event-success-done"
      >
        Done
      </button>
    </div>
  );
};

const EventCheckout = ({ event, onClose, onSuccess }) => {
  const dialogRef = useRef(null);
  const busyRef = useRef(false);
  const openerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);
  const [done, setDone] = useState(false);
  const handleSuccess = () => { setDone(true); onSuccess?.(); };

  useEffect(() => {
    const dialog = dialogRef.current;
    const focusable = () => dialog.querySelectorAll('button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"]), iframe');
    dialog.querySelector('h2')?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !busyRef.current) { onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    const opener = openerRef.current;
    return () => { document.removeEventListener('keydown', onKeyDown); opener?.focus?.(); };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => !busyRef.current && onClose()}>
      <motion.div
        ref={dialogRef}
        role="dialog" aria-modal="true" aria-labelledby="event-checkout-title"
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="glass-card w-full sm:max-w-[520px] max-h-[92vh] overflow-y-auto rounded-t-arch-xl sm:rounded-arch-xl p-6 sm:p-8 bg-background"
        style={{ background: 'var(--background)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="event-checkout"
      >
        {!done && <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="event-checkout-title" tabIndex={-1} className="font-heading text-2xl font-semibold text-text-main outline-none">Reserve your seat</h2>
            <p className="text-sm text-text-muted mt-1">{event.title} &middot; {formatEventDate(event, { weekday: 'short', month: 'short', day: 'numeric' })}, {formatEventTime(event)}</p>
          </div>
          <button onClick={onClose} aria-label="Close checkout" className="min-h-[44px] min-w-[44px] -mt-2 -mr-2 flex items-center justify-center rounded-full hover:bg-teal/10 text-text-muted">
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>}
        {done ? (
          <SuccessView event={event} onDone={onClose} />
        ) : (
          <Elements stripe={stripePromise}>
            <CheckoutForm event={event} onSuccess={handleSuccess} busyRef={busyRef} />
          </Elements>
        )}
      </motion.div>
    </div>
  );
};

export default EventCheckout;
